import { Alert } from '@app/components/core/alert/Alert'
import { PhaseType } from '@schema/phase'
import {
  InitiativeCreationStep,
  initiativeCreationSteps,
} from '@app/pages/dashboard/initiative/shared/workflow'
import { CreationGuide } from '../blocks/CreationGuide'
import { useEffect, useMemo, useState } from 'react'
import { useFormContext } from 'react-hook-form'
import {
  ImageEditRow,
  ImagePreview,
} from '@app/components/blocks/imageEditRow/ImageEditRow'
import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import { useClient } from '@based/sdk/react'
import { useTranslations } from '@i18n/useLanguage'
import { Style } from 'inlines'
import { useToast } from '@app/util/hooks/useToast'
import { Form } from '@app/components/core/form/Form'
import { TextField } from '@app/components/core/form/fields/TextField'
import { TextAreaField } from '@app/components/core/form/fields/TextAreaField'
import { enrich } from '@i18n/localizeHelper'
import type { InitiativeWithId } from '@app/pages/dashboard/initiative/shared/initiativeTypes'
import {
  createMainDetailsTextSchema,
  DESCRIPTION_MIN_LENGTH,
  type MainDetailsTextFormValues,
  VISION_MIN_LENGTH,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import { useInitiativeFormWatchAutosave } from '@app/pages/dashboard/initiative/shared/useInitiativeFormWatchAutosave'

const labelStyle: Style = {
  color: 'var(--brand-secondary)',
  fontWeight: 500,
}

const CURRENT_STEP = initiativeCreationSteps.mainDetails

export const MainDetailsStep = ({
  initiative,
  selectedPhaseType,
  completeStep,
  uncompleteStep,
}: {
  initiative: InitiativeWithId
  selectedPhaseType: PhaseType
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}) => {
  const translations = useTranslations().app.dashboard.initiative.creation
  const view = useTranslations().app.dashboard.initiative.view
  const formValidation = useTranslations().app.form.validation
  const client = useClient()
  const [addedImages, setAddedImages] = useState<ImagePreview[]>([])
  const [removedImages, setRemovedImages] = useState<number[]>([])
  const { showFailToast } = useToast()

  const schema = useMemo(
    () =>
      createMainDetailsTextSchema(
        view.initiativeNameMinLength,
        enrich(formValidation.minLength, DESCRIPTION_MIN_LENGTH),
        enrich(formValidation.minLength, VISION_MIN_LENGTH),
      ),
    [view.initiativeNameMinLength, formValidation.minLength],
  )

  const defaultValues = useMemo(
    (): MainDetailsTextFormValues => ({
      title: initiative.title ?? '',
      description: initiative.description ?? '',
      vision: initiative.vision ?? '',
    }),
    [initiative.title, initiative.description, initiative.vision],
  )

  useEffect(() => {
    if (removedImages.length > 0) {
      Promise.all(
        removedImages.map((fileId) => client.call('delete-file', { fileId })),
      )
        .then(() => setRemovedImages([]))
        .catch(() => showFailToast())
    }

    if (addedImages.length > 0) {
      Promise.all(
        addedImages.map((preview) => {
          try {
            return client.stream('upload-file', {
              mimeType: preview.file.type,
              contents: preview.file,
              size: preview.file.size,
              fileName: `initiative-media-${Date.now()}-${preview.file.name}`,
              payload: { initiativeImage: { id: initiative.id } },
            })
          } finally {
            URL.revokeObjectURL(preview.preview)
          }
        }),
      )
        .then(() => setAddedImages([]))
        .catch(() => showFailToast())
    }
  }, [
    client,
    initiative.id,
    addedImages,
    removedImages,
    setRemovedImages,
    setAddedImages,
    showFailToast,
  ])

  return (
    <CreationGuide
      selectedPhaseType={selectedPhaseType}
      currentStep={CURRENT_STEP}
    >
      <Form<MainDetailsTextFormValues>
        key={initiative.id}
        schema={schema}
        defaultValues={defaultValues}
        mode="onChange"
        onSubmit={() => undefined}
      >
        <Column gap="2.5rem" alignItems="stretch">
          <MainDetailsTextOnboardingEffects
            initiativeId={initiative.id}
            completeStep={completeStep}
            uncompleteStep={uncompleteStep}
          />
          <TextField<MainDetailsTextFormValues>
            name="title"
            label={translations.nameLabel}
            required={true}
            labelStyle={labelStyle}
            placeholder={translations.namePlaceholder}
          />
          <TextAreaField<MainDetailsTextFormValues>
            name="description"
            label={translations.sloganLabel}
            labelStyle={labelStyle}
            placeholder={translations.sloganPlaceholder}
          />
          <Column alignItems="stretch">
            <Column gap={0} alignItems="stretch">
              <Text.Medium style={labelStyle}>
                {translations.imagesLabel}
              </Text.Medium>
              <Text.Medium style={{ color: 'var(--palette-blue-500)' }}>
                {translations.imagesSubtitle}
              </Text.Medium>
            </Column>
            <ImageEditRow
              existingImages={initiative.images}
              newImages={addedImages}
              removedImages={removedImages}
              setNewImages={setAddedImages}
              setRemovedImages={setRemovedImages}
              iconStyle={{
                color: 'var(--palette-blue-400)',
              }}
            />
            <Alert
              variant="info"
              size="medium"
              message={translations.imagesInfoAlert}
            />
          </Column>
          <Column gap="0.38rem" alignItems="stretch">
            <TextAreaField<MainDetailsTextFormValues>
              name="vision"
              label={translations.contentLabel}
              labelStyle={labelStyle}
              placeholder={translations.contentPlaceholder}
            />
          </Column>
        </Column>
      </Form>
    </CreationGuide>
  )
}

const MainDetailsTextOnboardingEffects = ({
  initiativeId,
  completeStep,
  uncompleteStep,
}: {
  initiativeId: number
  completeStep: (step: InitiativeCreationStep) => void
  uncompleteStep: (step: InitiativeCreationStep) => void
}): null => {
  const { control, formState } = useFormContext<MainDetailsTextFormValues>()
  const { isValid } = formState

  useInitiativeFormWatchAutosave({
    initiativeId,
    control,
    toPayload: (v) => ({
      title: v.title,
      description: v.description,
      vision: v.vision,
      onboardingStep: CURRENT_STEP,
    }),
    watchDeps: (v) => [v.title, v.description, v.vision] as const,
  })

  useEffect(() => {
    if (isValid) {
      completeStep(CURRENT_STEP)
    } else {
      uncompleteStep(CURRENT_STEP)
    }
  }, [isValid, completeStep, uncompleteStep])

  return null
}
